/**
 * Static OOTB activity metadata used by the playbook transform path.
 *
 * These maps are intentionally checked in so the plugin does not need to load
 * built-in .now.js modules at runtime just to reverse-map sys_ids.
 */

/** Resolved variable info returned by the reverse maps */
export type ResolvedVariableType = 'string' | 'boolean' | 'number' | 'field_list' | 'glide_list' | 'template_value'
export type ResolvedVariable = { name: string; type: ResolvedVariableType }

// ============================================================================
// toShape direction (XML -> Fluent)
// ============================================================================

/** Reverse map: activity definition sys_id -> namespace path for generated code */
export const ACTIVITY_DEF_SYS_ID_TO_KEY: Record<string, string> = {
    b0c35e2b1b630010affd0e55cc4bcbca: 'ActivityDefinitions.Core.Instruction',
    c9839e38b701311004c164fdde11a904: 'ActivityDefinitions.Core.RecordForm',
    '569b840c1bbf0010affd0e55cc4bcbac': 'ActivityDefinitions.Core.UpdateRecord',
    '295188c01b3f0010affd0e55cc4bcb01': 'ActivityDefinitions.Core.CreateNewRecord',
    '24af882e435011107fd97ff8abb8f233': 'ActivityDefinitions.Core.NewRecordFormWithList',
    '0b7f23cf77045110612c1b1ad910612b': 'ActivityDefinitions.Core.RecordList',
    '226ed8f6c7ad5110bfbaf89f51c260b1': 'ActivityDefinitions.Core.Decision',
    ceac99e01b3f0010affd0e55cc4bcb83: 'ActivityDefinitions.Core.Placeholder',
    '7058962f1b630010affd0e55cc4bcbba': 'ActivityDefinitions.Core.KnowledgeArticle',
    '097b1efcb701311004c164fdde11a9dd': 'ActivityDefinitions.Core.AutocompletingRecordForm',
    '5009aca043713110333c145f15b8f212': 'ActivityDefinitions.Core.WaitForCondition',
    f16277690703001070e493d0fad30018: 'ActivityDefinitions.Core.NewRecordForm',
    caee4d471b0c1010affd0e55cc4bcbaf: 'ActivityDefinitions.Core.EmailForm',
    '51b422671ba30010affd0e55cc4bcbad': 'ActivityDefinitions.Core.SendEmail',
    d8f4d66b1b630010affd0e55cc4bcbc7: 'ActivityDefinitions.Core.TwoStepInstruction',
    da424d3f7f255210d9ed9b627c866524: 'ActivityDefinitions.Core.ChecklistTask',
    e1a2a6431b530150db4bba63cc4bcb97: 'ActivityDefinitions.Core.AskForMultiLevelApproval',
    a31c8f06a37201106e1d0ace26fcda76: 'ActivityDefinitions.Core.RequestManagerApproval',
    b54fdecb1b130150db4bba63cc4bcb58: 'ActivityDefinitions.Core.RequestAdHocApproval',
}

/**
 * Display preferences for inputs keyed by activity definition sys_id.
 *
 * Each entry is a Set of fluent input field names that should be emitted
 * during XML → Fluent transform. Fields NOT in this set (even if present in
 * sys_variable_value) are considered internal defaults and are omitted from
 * the generated Fluent code.
 *
 * When an activity definition sys_id is absent from this map (e.g. unknown
 * custom definitions), no filtering is applied and all inputs are emitted.
 */
export const ACTIVITY_DEF_INPUT_DISPLAY_PREFS: Record<string, Set<string>> = {
    // Instruction
    b0c35e2b1b630010affd0e55cc4bcbca: new Set(['message', 'wait']),
    // RecordForm
    c9839e38b701311004c164fdde11a904: new Set(['assigned_to', 'assignment_group']),
    // UpdateRecord
    '569b840c1bbf0010affd0e55cc4bcbac': new Set(['record', 'table_name', 'values']),
    // CreateNewRecord
    '295188c01b3f0010affd0e55cc4bcb01': new Set(['table_name', 'values']),
    // NewRecordFormWithList
    '24af882e435011107fd97ff8abb8f233': new Set(['wait']),
    // RecordList
    '0b7f23cf77045110612c1b1ad910612b': new Set(['assignment_group', 'assigned_to', 'wait']),
    // Placeholder — no inputs
    ceac99e01b3f0010affd0e55cc4bcb83: new Set([]),
    // KnowledgeArticle
    '7058962f1b630010affd0e55cc4bcbba': new Set([
        'assignment_group',
        'assigned_to',
        'title',
        'knowledge_article',
        'wait',
    ]),
    // AutocompletingRecordForm
    '097b1efcb701311004c164fdde11a9dd': new Set([
        'table',
        'record',
        'completion_condition',
        'assignment_group',
        'assigned_to',
    ]),
    // WaitForCondition
    '5009aca043713110333c145f15b8f212': new Set([
        'table',
        'record',
        'completion_condition',
        'assignment_group',
        'assigned_to',
    ]),
    // NewRecordForm
    f16277690703001070e493d0fad30018: new Set(['table', 'form_view', 'template_fields']),
    // EmailForm
    caee4d471b0c1010affd0e55cc4bcbaf: new Set([
        'wait',
        'to',
        'cc',
        'subject',
        'body',
        'assignment_group',
        'assigned_to',
        'to_email_address',
        'cc_email_address',
        'bcc',
        'bcc_email_address',
        'table',
        'target_record',
    ]),
    // SendEmail
    '51b422671ba30010affd0e55cc4bcbad': new Set(['to', 'cc', 'bcc', 'subject', 'body', 'target_record']),
    // TwoStepInstruction
    d8f4d66b1b630010affd0e55cc4bcbc7: new Set([
        'assignment_group',
        'assigned_to',
        'initial_message',
        'completed_message',
        'skipped_message',
    ]),
    // ChecklistTask
    da424d3f7f255210d9ed9b627c866524: new Set([
        'checklist_items',
        'checklist_name',
        'task',
        'show_sla',
        'can_skip',
        'assignment_group',
        'assigned_to',
        'checklist_owner',
    ]),
    // AskForMultiLevelApproval
    e1a2a6431b530150db4bba63cc4bcb97: new Set([
        'assigned_to',
        'assigned_to_comments',
        'assigned_to_due_date',
        'table',
        'record',
    ]),
    // RequestManagerApproval
    a31c8f06a37201106e1d0ace26fcda76: new Set(['table', 'variable', 'approver', 'due_date', 'comments']),
    // RequestAdHocApproval
    b54fdecb1b130150db4bba63cc4bcb58: new Set(['approver', 'approver_group', 'table', 'record']),
}

/**
 * Display preferences for experience properties keyed by activity definition sys_id.
 *
 * Each entry is a Set of fluent experience property field names that should be
 * emitted during XML → Fluent transform. Fields NOT in this set (even if present
 * in sys_variable_value) are considered internal defaults and are omitted.
 *
 * When an activity definition sys_id is absent from this map, no filtering is
 * applied and all experience properties are emitted.
 */
export const ACTIVITY_DEF_EXPERIENCE_DISPLAY_PREFS: Record<string, Set<string>> = {
    // Instruction
    b0c35e2b1b630010affd0e55cc4bcbca: new Set([
        'associated_table',
        'associated_record',
        'experience_status_table',
        'experience_status_record',
        'tagline',
        'icon',
        'title',
        'description',
        'footer',
        'is_automated',
    ]),
    // RecordForm
    c9839e38b701311004c164fdde11a904: new Set([
        'associated_table',
        'associated_record',
        'experience_status_table',
        'experience_status_record',
        'tagline',
        'icon',
        'title',
        'description',
        'pending_title',
        'pending_description',
        'record_fields',
        'footer',
        'form_view',
        'form_fields',
        'attachment_source',
        'attachments_read_only',
        'show_sla',
        'show_checklist',
        'is_automated',
    ]),
    // UpdateRecord
    '569b840c1bbf0010affd0e55cc4bcbac': new Set([
        'associated_table',
        'associated_record',
        'experience_status_table',
        'experience_status_record',
        'tagline',
        'icon',
        'title',
        'description',
        'pending_title',
        'pending_description',
        'record_fields',
        'footer',
        'form_view',
        'form_fields',
        'attachment_source',
        'attachments_read_only',
        'show_sla',
        'show_checklist',
        'is_automated',
    ]),
    // CreateNewRecord
    '295188c01b3f0010affd0e55cc4bcb01': new Set([
        'associated_table',
        'associated_record',
        'experience_status_table',
        'experience_status_record',
        'tagline',
        'icon',
        'title',
        'description',
        'pending_title',
        'pending_description',
        'record_fields',
        'footer',
        'form_view',
        'form_fields',
        'attachment_source',
        'attachments_read_only',
        'show_sla',
        'show_checklist',
        'is_automated',
    ]),
    // NewRecordFormWithList
    '24af882e435011107fd97ff8abb8f233': new Set([
        'associated_table',
        'associated_record',
        'experience_status_table',
        'experience_status_record',
        'tagline',
        'icon',
        'title',
        'description',
        'record_fields',
        'list_title',
        'table',
        'list_query',
        'ui_view',
        'columns',
        'max_columns',
        'row_count',
        'is_automated',
    ]),
    // RecordList
    '0b7f23cf77045110612c1b1ad910612b': new Set([
        'associated_table',
        'associated_record',
        'experience_status_table',
        'experience_status_record',
        'tagline',
        'icon',
        'title',
        'description',
        'record_fields',
        'list_title',
        'table',
        'list_query',
        'ui_view',
        'columns',
        'max_columns',
        'row_count',
        'is_automated',
    ]),
    // Placeholder — no experience properties
    ceac99e01b3f0010affd0e55cc4bcb83: new Set([]),
    // KnowledgeArticle
    '7058962f1b630010affd0e55cc4bcbba': new Set([
        'associated_table',
        'associated_record',
        'experience_status_table',
        'experience_status_record',
        'knowledge_table',
        'knowledge_record',
        'title',
        'footer',
        'is_automated',
    ]),
    // AutocompletingRecordForm
    '097b1efcb701311004c164fdde11a9dd': new Set([
        'associated_table',
        'associated_record',
        'experience_status_table',
        'experience_status_record',
        'tagline',
        'icon',
        'title',
        'description',
        'pending_title',
        'pending_description',
        'record_fields',
        'footer',
        'form_view',
        'form_fields',
        'attachment_source',
        'attachments_read_only',
        'show_sla',
        'show_checklist',
        'is_automated',
    ]),
    // WaitForCondition
    '5009aca043713110333c145f15b8f212': new Set([
        'associated_table',
        'associated_record',
        'experience_status_table',
        'experience_status_record',
        'tagline',
        'icon',
        'title',
        'description',
        'pending_title',
        'pending_description',
        'record_fields',
        'footer',
        'form_view',
        'form_fields',
        'attachment_source',
        'attachments_read_only',
        'show_sla',
        'show_checklist',
        'is_automated',
    ]),
    // NewRecordForm
    f16277690703001070e493d0fad30018: new Set([
        'associated_table',
        'associated_record',
        'experience_status_table',
        'experience_status_record',
        'tagline',
        'icon',
        'title',
        'description',
        'pending_description',
        'pending_title',
        'record_fields',
        'footer',
        'form_view',
        'form_fields',
        'template_fields',
        'attachment_source',
        'attachments_read_only',
        'show_sla',
        'show_checklist',
        'is_automated',
    ]),
    // EmailForm
    caee4d471b0c1010affd0e55cc4bcbaf: new Set([
        'associated_table',
        'associated_record',
        'experience_status_table',
        'experience_status_record',
        'tagline',
        'icon',
        'title',
        'description',
        'pending_title',
        'pending_description',
        'record_fields',
        'footer',
        'form_view',
        'form_fields',
        'attachment_source',
        'attachments_read_only',
        'show_sla',
        'show_checklist',
        'is_automated',
    ]),
    // SendEmail
    '51b422671ba30010affd0e55cc4bcbad': new Set([
        'associated_table',
        'associated_record',
        'experience_status_table',
        'experience_status_record',
        'tagline',
        'icon',
        'title',
        'description',
        'pending_title',
        'pending_description',
        'record_fields',
        'footer',
        'form_view',
        'form_fields',
        'attachment_source',
        'attachments_read_only',
        'show_sla',
        'show_checklist',
        'is_automated',
    ]),
    // TwoStepInstruction
    d8f4d66b1b630010affd0e55cc4bcbc7: new Set([
        'associated_table',
        'associated_record',
        'experience_status_table',
        'experience_status_record',
        'tagline',
        'icon',
        'title',
        'description',
        'footer',
        'is_automated',
    ]),
    // ChecklistTask
    da424d3f7f255210d9ed9b627c866524: new Set([
        'associated_table',
        'associated_record',
        'experience_status_table',
        'experience_status_record',
        'tagline',
        'icon',
        'title',
        'description',
        'pending_title',
        'pending_description',
        'record_fields',
        'footer',
        'form_view',
        'form_fields',
        'attachment_source',
        'attachments_read_only',
        'show_sla',
        'show_checklist',
        'is_automated',
    ]),
    // AskForMultiLevelApproval — no experienceDisplayPreferences defined (all experience props are defaults)
    e1a2a6431b530150db4bba63cc4bcb97: new Set([]),
    // RequestManagerApproval — no experienceDisplayPreferences defined
    a31c8f06a37201106e1d0ace26fcda76: new Set([]),
    // RequestAdHocApproval — no experienceDisplayPreferences defined
    b54fdecb1b130150db4bba63cc4bcb58: new Set([]),
}

/** Namespace key for Decision activity definition — used for callee matching */
export const DECISION_ACTIVITY_DEF_KEY = 'ActivityDefinitions.Core.Decision'

/** Decision activity definition sys_id — used for special-case handling in the activity plugin */
export const DECISION_DEF_SYS_ID = '226ed8f6c7ad5110bfbaf89f51c260b1'

/** Variable sys_id for the Decision branches input */
export const DECISION_BRANCHES_VAR_SYS_ID = '074e5876c7ad5110bfbaf89f51c26087'

/**
 * Reverse map: input variable sys_id -> { name, type }.
 *
 * Covers inputs from all OOTB backing flows and actions:
 * - SimpleInstructionFlow: message, wait
 * - ManualActivityFlow: assignedTo, assignmentGroup, wait
 * - CollectDataActivityFlow: assignedTo, assignmentGroup, dataDefinition, wait
 * - UpdateRecordAction: values, record, tableName
 * - CreateRecordAction: values, tableName
 *
 * Duplicate sysIds across different backing defs are fine — the same sysId always
 * maps to the same name.
 */
export const INPUT_SYS_ID_TO_FLUENT: Record<string, ResolvedVariable> = {
    '6c9c4ee31b630010affd0e55cc4bcbbf': {
        name: 'message',
        type: 'string',
    },
    c23322e91bbb001094630f28cc4bcbae: {
        name: 'wait',
        type: 'string',
    },
    '3499c6631b630010affd0e55cc4bcb90': {
        name: 'assigned_to',
        type: 'string',
    },
    '4f9fdae51bbb001094630f28cc4bcb66': {
        name: 'assignment_group',
        type: 'string',
    },
    '024026291bbb001094630f28cc4bcb03': {
        name: 'wait',
        type: 'string',
    },
    '8d839b4b1b805010affd0e55cc4bcb93': {
        name: 'assigned_to',
        type: 'string',
    },
    '05839b4b1b805010affd0e55cc4bcb8a': {
        name: 'assignment_group',
        type: 'string',
    },
    '26e45b8b1b805010affd0e55cc4bcb4e': {
        name: 'data_definition',
        type: 'string',
    },
    c9839b4b1b805010affd0e55cc4bcbb2: {
        name: 'wait',
        type: 'string',
    },
    e7d270ccc3c632002841b63b12d3ae8b: {
        name: 'values',
        type: 'template_value',
    },
    e3d270ccc3c632002841b63b12d3ae98: {
        name: 'record',
        type: 'string',
    },
    '1fd27c8cc3c632002841b63b12d3aeb9': {
        name: 'table_name',
        type: 'string',
    },
    '8b52fc8cc3c632002841b63b12d3ae36': {
        name: 'values',
        type: 'template_value',
    },
    '0352fc8cc3c632002841b63b12d3ae12': {
        name: 'table_name',
        type: 'string',
    },
    '0f4e18f6c7ad5110bfbaf89f51c260f0': {
        name: 'type',
        type: 'string',
    },
    '074e5876c7ad5110bfbaf89f51c26087': {
        name: 'branches',
        type: 'string',
    },
    e11d767eff50311029c46b6d253bf192: {
        name: 'table',
        type: 'string',
    },
    '611d767eff50311029c46b6d253bf199': {
        name: 'record',
        type: 'string',
    },
    '651d767eff50311029c46b6d253bf19c': {
        name: 'completion_condition',
        type: 'string',
    },
    '711d7a7eff50311029c46b6d253bf1cd': {
        name: 'assignment_group',
        type: 'string',
    },
    b51d7a7eff50311029c46b6d253bf1d1: {
        name: 'assigned_to',
        type: 'string',
    },
    a1e8dea4db9f0010efc65404ce9619f8: {
        name: 'table',
        type: 'string',
    },
    '79e81ae4db9f0010efc65404ce96192a': {
        name: 'form_view',
        type: 'string',
    },
    '7b04659dff412010748653bd6338f1b0': {
        name: 'template_fields',
        type: 'string',
    },
    df09be471b405010affd0e55cc4bcb45: {
        name: 'assignment_group',
        type: 'string',
    },
    '5b09be471b405010affd0e55cc4bcb4f': {
        name: 'assigned_to',
        type: 'string',
    },
    '9309be471b405010affd0e55cc4bcb58': {
        name: 'to',
        type: 'glide_list',
    },
    d54898f4532220109f15ddeeff7b1276: {
        name: 'to_email_address',
        type: 'string',
    },
    eb09be471b405010affd0e55cc4bcb60: {
        name: 'cc',
        type: 'glide_list',
    },
    dd4898f4532220109f15ddeeff7b12aa: {
        name: 'cc_email_address',
        type: 'string',
    },
    '2709be471b405010affd0e55cc4bcb69': {
        name: 'bcc',
        type: 'glide_list',
    },
    '5d4898f4532220109f15ddeeff7b12b7': {
        name: 'bcc_email_address',
        type: 'string',
    },
    '6f09be471b405010affd0e55cc4bcb71': {
        name: 'subject',
        type: 'string',
    },
    a709be471b405010affd0e55cc4bcb77: {
        name: 'body',
        type: 'string',
    },
    e8e44799536220109f15ddeeff7b1250: {
        name: 'table',
        type: 'string',
    },
    ef09be471b405010affd0e55cc4bcb7c: {
        name: 'target_record',
        type: 'string',
    },
    '2b09be471b405010affd0e55cc4bcb85': {
        name: 'wait',
        type: 'string',
    },
    bf8226e31ba30010affd0e55cc4bcb5e: {
        name: 'to',
        type: 'glide_list',
    },
    a8e081cd53e220109f15ddeeff7b1293: {
        name: 'to_email_address',
        type: 'string',
    },
    c49226e31ba30010affd0e55cc4bcb83: {
        name: 'cc',
        type: 'glide_list',
    },
    e8e081cd53e220109f15ddeeff7b12a6: {
        name: 'cc_email_address',
        type: 'string',
    },
    '049226e31ba30010affd0e55cc4bcb8b': {
        name: 'bcc',
        type: 'glide_list',
    },
    a8e081cd53e220109f15ddeeff7b12b3: {
        name: 'bcc_email_address',
        type: 'string',
    },
    '089226e31ba30010affd0e55cc4bcb9f': {
        name: 'subject',
        type: 'string',
    },
    c89226e31ba30010affd0e55cc4bcba4: {
        name: 'body',
        type: 'string',
    },
    '8c9226e31ba30010affd0e55cc4bcba9': {
        name: 'target_record',
        type: 'string',
    },
    cef40b99536220109f15ddeeff7b12a3: {
        name: 'table',
        type: 'string',
    },
    f8b3aae91bbb001094630f28cc4bcb2b: {
        name: 'assignment_group',
        type: 'string',
    },
    ffcd02671b630010affd0e55cc4bcbdf: {
        name: 'assigned_to',
        type: 'string',
    },
    a73fce671b630010affd0e55cc4bcbbb: {
        name: 'initial_message',
        type: 'string',
    },
    '233fce671b630010affd0e55cc4bcbcd': {
        name: 'completed_message',
        type: 'string',
    },
    e33fce671b630010affd0e55cc4bcbd2: {
        name: 'skipped_message',
        type: 'string',
    },
    '65e9b08e7f795210086a9b627c8665ee': {
        name: 'checklist_owner',
        type: 'string',
    },
    c213d6667fb99210086a9b627c8665ac: {
        name: 'checklist_items',
        type: 'string',
    },
    '42fd14f47f711210086a9b627c866508': {
        name: 'checklist_name',
        type: 'string',
    },
    e00efc3b7fa15210d9ed9b627c8665eb: {
        name: 'task',
        type: 'string',
    },
    '280efc3b7fa15210d9ed9b627c8665ee': {
        name: 'show_sla',
        type: 'boolean',
    },
    '2c0efc3b7fa15210d9ed9b627c8665f2': {
        name: 'can_skip',
        type: 'boolean',
    },
    a80e307b7fa15210d9ed9b627c86650f: {
        name: 'assignment_group',
        type: 'string',
    },
    bc0e307b7fa15210d9ed9b627c866512: {
        name: 'assigned_to',
        type: 'string',
    },
    d54f6c471b97cd10db4bba63cc4bcb0f: {
        name: 'assigned_to',
        type: 'string',
    },
    '594f6c471b97cd10db4bba63cc4bcb18': {
        name: 'assigned_to_comments',
        type: 'string',
    },
    '514f6c471b97cd10db4bba63cc4bcb28': {
        name: 'assigned_to_due_date',
        type: 'string',
    },
    a14f6c471b97cd10db4bba63cc4bcb2b: {
        name: 'table',
        type: 'string',
    },
    e14f6c471b97cd10db4bba63cc4bcb2e: {
        name: 'record',
        type: 'string',
    },
    '1ec899171b934150db4bba63cc4bcb3a': {
        name: 'table',
        type: 'string',
    },
    '5ac899171b934150db4bba63cc4bcb2b': {
        name: 'approver',
        type: 'glide_list',
    },
    a86e10711bbbc1d0ca592f092a4bcb91: {
        name: 'approver_group',
        type: 'string',
    },
    c3aeb10a1bf749d0db4bba63cc4bcbc0: {
        name: 'record',
        type: 'string',
    },
    b0258702a37201106e1d0ace26fcda16: {
        name: 'table',
        type: 'string',
    },
    bc6abcd9a39b011038310ace26fcda9d: {
        name: 'variable',
        type: 'string',
    },
    '78258702a37201106e1d0ace26fcda21': {
        name: 'approver',
        type: 'string',
    },
    '7c258702a37201106e1d0ace26fcda29': {
        name: 'due_date',
        type: 'string',
    },
    '7c258702a37201106e1d0ace26fcda2e': {
        name: 'comments',
        type: 'string',
    },
    '197e16a51bbb001094630f28cc4bcb4e': {
        name: 'assignment_group',
        type: 'string',
    },
    fd0756eb1b630010affd0e55cc4bcbbd: {
        name: 'assigned_to',
        type: 'string',
    },
    '310756eb1b630010affd0e55cc4bcbc5': {
        name: 'title',
        type: 'string',
    },
    '1087daeb1b630010affd0e55cc4bcbee': {
        name: 'knowledge_article',
        type: 'string',
    },
    c3e49aad1b7b001094630f28cc4bcbd1: {
        name: 'wait',
        type: 'string',
    },
}

/** Reverse map: experience property variable sys_id -> { name, type }. */
export const EXPERIENCE_PROPERTY_SYS_ID_TO_FLUENT: Record<string, ResolvedVariable> = {
    eb0474b8c763001085f5532bb4c260f5: {
        name: 'associated_record',
        type: 'string',
    },
    '78f334b8c763001085f5532bb4c26009': {
        name: 'associated_table',
        type: 'string',
    },
    '267e68875b00001070e4492c11f91a34': {
        name: 'description',
        type: 'string',
    },
    '55628762c3211010381b60bc0eba8fec': {
        name: 'experience_status_record',
        type: 'string',
    },
    '9c6b7e22c3211010381b60bc0eba8f98': {
        name: 'experience_status_table',
        type: 'string',
    },
    '8793d6feb70100101a6ddb91de11a98f': {
        name: 'footer',
        type: 'string',
    },
    '24bdf44673030010fe4a6da44cf6a707': {
        name: 'icon',
        type: 'string',
    },
    ec5d96eac7e60010bfbaf89f51c2605d: {
        name: 'is_automated',
        type: 'boolean',
    },
    '661d304673030010fe4a6da44cf6a7b1': {
        name: 'tagline',
        type: 'string',
    },
    '49ed24875b00001070e4492c11f91aa5': {
        name: 'title',
        type: 'string',
    },
    '8c1ef8e40f100010f5bf5ae9c4767ed6': {
        name: 'associated_record',
        type: 'string',
    },
    '77edf4e40f100010f5bf5ae9c4767edb': {
        name: 'associated_table',
        type: 'string',
    },
    '4f0e5d7e736100104a905ee515f6a70b': {
        name: 'attachment_source',
        type: 'string',
    },
    '576de7780f1300102920c912d4767e52': {
        name: 'attachments_read_only',
        type: 'boolean',
    },
    '1d27b10ac7200010bfbaf89f51c26016': {
        name: 'description',
        type: 'string',
    },
    '5e5272b2c3211010381b60bc0eba8f13': {
        name: 'experience_status_record',
        type: 'string',
    },
    '4612be72c3211010381b60bc0eba8f47': {
        name: 'experience_status_table',
        type: 'string',
    },
    '71ae56b6b74100101a6ddb91de11a9a1': {
        name: 'footer',
        type: 'string',
    },
    fbc1f11b5b40001070e4492c11f91a45: {
        name: 'form_fields',
        type: 'field_list',
    },
    '7cb449e85b41001070e4492c11f91a06': {
        name: 'form_view',
        type: 'string',
    },
    ad4db04673030010fe4a6da44cf6a798: {
        name: 'icon',
        type: 'string',
    },
    '63fd1eeac7e60010bfbaf89f51c26059': {
        name: 'is_automated',
        type: 'boolean',
    },
    '6688ff5d0700101070e493d0fad300f6': {
        name: 'pending_description',
        type: 'string',
    },
    e9ae23190700101070e493d0fad300c1: {
        name: 'pending_title',
        type: 'string',
    },
    '05bee8875b00001070e4492c11f91afb': {
        name: 'record_fields',
        type: 'field_list',
    },
    d803775a5b01001031a67d6f7281c7aa: {
        name: 'show_checklist',
        type: 'boolean',
    },
    '54c49bc60f3200102920c912d4767ee2': {
        name: 'show_sla',
        type: 'boolean',
    },
    '9a0c340673030010fe4a6da44cf6a7de': {
        name: 'tagline',
        type: 'string',
    },
    '13597d8ac7200010bfbaf89f51c2609e': {
        name: 'title',
        type: 'string',
    },
    d18061f287d300102252c88fc0e3eccb: {
        name: 'associated_record',
        type: 'string',
    },
    '5fa0e1f287d300102252c88fc0e3ec66': {
        name: 'associated_table',
        type: 'string',
    },
    ca4591f25b21001070e4492c11f91a01: {
        name: 'columns',
        type: 'field_list',
    },
    '47ceb192777220102a272f589a10616d': {
        name: 'description',
        type: 'string',
    },
    '9d2ff6a7c3611010381b60bc0eba8f3c': {
        name: 'experience_status_record',
        type: 'string',
    },
    '8cfe36a7c3611010381b60bc0eba8fd2': {
        name: 'experience_status_table',
        type: 'string',
    },
    '3f9d744673030010fe4a6da44cf6a7b9': {
        name: 'icon',
        type: 'string',
    },
    '091e9aeac7e60010bfbaf89f51c260ea': {
        name: 'is_automated',
        type: 'boolean',
    },
    '6f2f7ecf5b21001070e4492c11f91a77': {
        name: 'list_query',
        type: 'string',
    },
    '12e9e761870300102252c88fc0e3ecb2': {
        name: 'list_title',
        type: 'string',
    },
    d6d1f7585b92001022527d6f7281c7cc: {
        name: 'max_columns',
        type: 'string',
    },
    bc00dc415ba2001022527d6f7281c7c5: {
        name: 'record_fields',
        type: 'field_list',
    },
    '66c5b3885b16001022527d6f7281c77c': {
        name: 'row_count',
        type: 'string',
    },
    '29255db25b21001070e4492c11f91ac5': {
        name: 'table',
        type: 'string',
    },
    '01fcbc0673030010fe4a6da44cf6a7d6': {
        name: 'tagline',
        type: 'string',
    },
    '1d8b12fe5b61001070e4492c11f91a19': {
        name: 'title',
        type: 'string',
    },
    '320f94505b92001022527d6f7281c7e1': {
        name: 'ui_view',
        type: 'string',
    },
    '3b81ced9b77500101a6ddb91de11a9b1': {
        name: 'associated_table',
        type: 'string',
    },
    f8a48a5db77500101a6ddb91de11a950: {
        name: 'associated_record',
        type: 'string',
    },
    '58017272c3211010381b60bc0eba8fd1': {
        name: 'experience_status_table',
        type: 'string',
    },
    c641b672c3211010381b60bc0eba8f6c: {
        name: 'experience_status_record',
        type: 'string',
    },
    '7e14425db77500101a6ddb91de11a938': {
        name: 'knowledge_table',
        type: 'string',
    },
    '33bef999b77500101a6ddb91de11a925': {
        name: 'knowledge_record',
        type: 'string',
    },
    '9d3bd5bac7120010bfbaf89f51c260bb': {
        name: 'title',
        type: 'string',
    },
    '307e92b6b74100101a6ddb91de11a9b4': {
        name: 'footer',
        type: 'string',
    },
    '98cd9aeac7e60010bfbaf89f51c2602e': {
        name: 'is_automated',
        type: 'boolean',
    },
    '6c6499fbff001010393e53bd6338f1c8': {
        name: 'associated_table',
        type: 'string',
    },
    '346499fbff001010393e53bd6338f1eb': {
        name: 'associated_record',
        type: 'string',
    },
    '737ebe67c3611010381b60bc0eba8ff9': {
        name: 'experience_status_table',
        type: 'string',
    },
    '25be72a7c3611010381b60bc0eba8f71': {
        name: 'experience_status_record',
        type: 'string',
    },
    '786499fbff001010393e53bd6338f1fc': {
        name: 'tagline',
        type: 'string',
    },
    fc64d9fbff001010393e53bd6338f10d: {
        name: 'icon',
        type: 'string',
    },
    '986499fbff001010393e53bd6338f13d': {
        name: 'title',
        type: 'string',
    },
    '546499fbff001010393e53bd6338f14e': {
        name: 'description',
        type: 'string',
    },
    e86499fbff001010393e53bd6338f1ab: {
        name: 'pending_description',
        type: 'string',
    },
    '7864d9fbff001010393e53bd6338f138': {
        name: 'pending_title',
        type: 'string',
    },
    '906459fbff001010393e53bd6338f1ce': {
        name: 'record_fields',
        type: 'field_list',
    },
    e86499fbff001010393e53bd6338f1b7: {
        name: 'footer',
        type: 'string',
    },
    '306499fbff001010393e53bd6338f1da': {
        name: 'form_view',
        type: 'string',
    },
    '0964d9fbff001010393e53bd6338f151': {
        name: 'form_fields',
        type: 'field_list',
    },
    '07a68b3dff812010748653bd6338f153': {
        name: 'template_fields',
        type: 'string',
    },
    '586499fbff001010393e53bd6338f16b': {
        name: 'attachment_source',
        type: 'string',
    },
    '246499fbff001010393e53bd6338f188': {
        name: 'attachments_read_only',
        type: 'boolean',
    },
    a46499fbff001010393e53bd6338f177: {
        name: 'show_sla',
        type: 'boolean',
    },
    fc64d9fbff001010393e53bd6338f11f: {
        name: 'show_checklist',
        type: 'boolean',
    },
    ac6499fbff001010393e53bd6338f199: {
        name: 'is_automated',
        type: 'boolean',
    },
}
